import PropTypes from 'prop-types'
import React from 'react'
import omit from 'lodash-es/omit'

import { toTitle } from '../../../common/utils/formatString'
import {
  getQueryWithoutCategory,
  getQueryWithoutOption
} from '../../../common/utils/urlHelper'
import filterParamsConfig from '../../../common/constants/filterParamsConfig'

const createLabelMapping = () => {
  const prepend = (labels, prefix) => {
    const prefixedLabels = {}
    Object.keys(labels).forEach(key => {
      prefixedLabels[key] = `${prefix} ${labels[key]}`
    })
    return prefixedLabels
  }

  const sizeLabels = prepend(filterParamsConfig.sizes.labels, 'Size:')
  const salesLabels = prepend(filterParamsConfig.sales.labels, 'Sales:')
  const filesIncluded = prepend(
    filterParamsConfig.files_included.labels,
    'Includes:'
  )
  return Object.assign(sizeLabels, salesLabels, filesIncluded)
}

const FilterList = ({
  FilterItem,
  parsedLocation,
  param,
  className,
  interactive = true
}) => {
  // if the fitler is not in the config, simply return null
  if (!filterParamsConfig[param] || !parsedLocation[param]) return null

  if (filterParamsConfig[param].isMultiple) {
    const labelMapping = createLabelMapping()
    const filters = parsedLocation[param].split(
      filterParamsConfig[param].delimiter
    )
    return (
      <ul className={className}>
        {filters.map((option, index) => {
          let newQuery
          let displayName = labelMapping[option] || toTitle(option)

          // category is separate by '/' eg. 'category/wordpress/clean'
          if (filterParamsConfig[param].delimiter === '/') {
            newQuery = getQueryWithoutCategory(parsedLocation, param, option)
            // tags
          } else if (filterParamsConfig[param].delimiter === ',') {
            newQuery = getQueryWithoutOption(parsedLocation, param, option)
          }
          // reset page param
          delete newQuery.page
          if (displayName) {
            if (index < filters.length - 1 && !interactive) {
              displayName += param === 'category' ? ' /' : ','
            }

            return (
              <FilterItem
                paramType={param}
                displayName={displayName}
                locationParams={newQuery}
                key={index}
                index={index}
                interactive={interactive}
              />
            )
          } else {
            return null
          }
        })}
      </ul>
    )
  } else {
    if (filterParamsConfig[param].inRangeWith) {
      const minRangeParam = filterParamsConfig[param].inRangeWith.min || param
      const maxRangeParam = filterParamsConfig[param].inRangeWith.max || param
      const minParamValue = parsedLocation[minRangeParam]
      const maxParamValue = parsedLocation[maxRangeParam]

      // combine price_min and price_max if both exist
      if (minParamValue && maxParamValue) {
        const minParamFormatValue = filterParamsConfig[param].format
          ? filterParamsConfig[param].format(minParamValue)
          : minParamValue
        const maxParamFormatValue = filterParamsConfig[param].format
          ? filterParamsConfig[param].format(maxParamValue)
          : maxParamValue
        const priceDisplayName = `${minParamFormatValue} - ${maxParamFormatValue}`
        const newQuery = omit(parsedLocation, [
          minRangeParam,
          maxRangeParam,
          'page'
        ])
        return (
          <ul className={className}>
            <FilterItem
              paramType={param}
              displayName={priceDisplayName}
              locationParams={newQuery}
              index={0}
              interactive={interactive}
            />
          </ul>
        )
      } else {
        const paramFormatValue = filterParamsConfig[param].format
          ? filterParamsConfig[param].format(parsedLocation[param])
          : parsedLocation[param]
        const priceDisplayName = `${
          filterParamsConfig[param].label
        } ${paramFormatValue}`
        const newQuery = omit(parsedLocation, [param, 'page'])
        return (
          <ul className={className}>
            <FilterItem
              paramType={param}
              displayName={priceDisplayName}
              locationParams={newQuery}
              index={0}
              interactive={interactive}
            />
          </ul>
        )
      }
    } else {
      let label = parsedLocation[param]
      let newQuery = omit(parsedLocation, [param, 'page'])
      if (param === 'sort_by' && parsedLocation['sort_direction']) {
        // parse label for sort_by filter
        label =
          parsedLocation['sort_by'] + '-' + parsedLocation['sort_direction']
        // when it's a sort by filter, we remove the `sort_direction`
        // as well when clear
        newQuery = omit(parsedLocation, [param, 'page', 'sort_direction'])
      }
      let displayName = !filterParamsConfig[param].labels
        ? parsedLocation[param]
        : filterParamsConfig[param].labels[label]

      if (filterParamsConfig[param].format) {
        displayName = filterParamsConfig[param].format(displayName)
      }

      return (
        <ul className={className}>
          <FilterItem
            paramType={param}
            displayName={displayName}
            locationParams={newQuery}
            index={0}
            interactive={interactive}
          />
        </ul>
      )
    }
  }
}

FilterList.propTypes = {
  parsedLocation: PropTypes.object.isRequired,
  param: PropTypes.string.isRequired
}

export default FilterList
